import React, { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import { bool } from 'prop-types';
import { useHistory } from 'react-router-dom';
import { BlockWrap } from '../../atoms';
import { signUpCompanyDetailsFields, signUpCompanyDetailsNames, initialValues } from './signUpCompanyDetailsSchema';
import CustomSelectOnboarding from './CustomSelectOnboarding';
import { FieldsWrapper } from '../formStyles';
import InputOnboarding from '../_common/InputOnboarding';
import { SELECT_OPTIONS, CUSTOM_SELECT_PROPS } from './consts';
import { isDefined } from '../../../utils/helpers';
import SelectWithOthers from './SelectWithOthers';
import { useRegistrationStorage } from '../../../hooks/registration';
import { ONBOARDING_STATUS, ONBOARDING_STATUS_WITH_ROUTES } from '../../../constants/registration';
import { UI_ROUTES } from '../../../constants/routes';
import { signUpCompanyDetailsValidator } from './signUpCompanyDetailsValidator';
import SignUpFooter from '../SignUpPersonalDetailsForm/SignUpFooter';
import useDynamicValidation from '../../../hooks/useDynamicValidation';

const COMPONENTS_MAP = {
  [signUpCompanyDetailsNames.companyName]: InputOnboarding,
  [signUpCompanyDetailsNames.companySize]: CustomSelectOnboarding,
  [signUpCompanyDetailsNames.companyIndustry]: SelectWithOthers,
};

const COMPONENTS_PROPS_MAP = {
  [signUpCompanyDetailsNames.companyName]: { customLabel: true },
  [signUpCompanyDetailsNames.companySize]: {},
  [signUpCompanyDetailsNames.companyIndustry]: {},
  [signUpCompanyDetailsNames.companyIndustryOther]: '',
};

const SignUpCompanyDetailsForm = () => {
  const [registrationSession, setRegistrationSession] = useRegistrationStorage();
  const { push } = useHistory();
  const [showOtherInput, setShowOtherInput] = useState(false);
  const validationObject = useDynamicValidation(signUpCompanyDetailsValidator, showOtherInput);

  const handleSubmit = useCallback(
    (values) => {
      setRegistrationSession({
        ...registrationSession,
        [ONBOARDING_STATUS.companyDetails]: values,
        onboardingStatus: ONBOARDING_STATUS.acceptingPayments,
      });
      push(UI_ROUTES.signUpAcceptingPayments);
    },
    [setRegistrationSession, registrationSession, push]
  );

  const parsedInitialValues = {
    ...initialValues,
    ...(registrationSession?.[ONBOARDING_STATUS.companyDetails] ?? {}),
  };

  return (
    <BlockWrap margin="37px 0 0">
      <FieldsWrapper>
        <Formik initialValues={parsedInitialValues} validate={validationObject} onSubmit={handleSubmit} validateOnBlur>
          <Form>
            {signUpCompanyDetailsFields.map(({ field, type, label, ...rest }, index) => {
              const Component = COMPONENTS_MAP[field] || InputOnboarding;
              const addCustomMargin = field === signUpCompanyDetailsNames.companySize;

              return (
                <Component
                  key={field}
                  name={field}
                  {...(isDefined(SELECT_OPTIONS[field]) && {
                    options: SELECT_OPTIONS[field],
                    ...CUSTOM_SELECT_PROPS,
                  })}
                  type={type}
                  label={label}
                  index={index}
                  {...COMPONENTS_PROPS_MAP[field]}
                  {...rest}
                  {...(addCustomMargin && {
                    errorProps: {
                      margin: '6px 0 14px 0',
                    },
                  })}
                  setShowOtherInput={setShowOtherInput}
                />
              );
            })}

            <SignUpFooter
              primaryButtonText="common.continueText"
              previousRoute={ONBOARDING_STATUS_WITH_ROUTES.personalDetails}
            />
          </Form>
        </Formik>
      </FieldsWrapper>
    </BlockWrap>
  );
};

SignUpCompanyDetailsForm.propTypes = { loading: bool };

SignUpCompanyDetailsForm.defaultProps = { loading: false };

export default SignUpCompanyDetailsForm;
